import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FormPageShell } from "../../components/data/FormPageShell";
import { ItemSectionBasic } from "./ItemSectionBasic";
import { ItemSectionPrice } from "./ItemSectionPrice";
import { ItemSectionAccounting } from "./ItemSectionAccounting";
import { ItemSectionStock } from "./ItemSectionStock";
import { useBranchScope } from "./branchScope";
import { createItem, listItemTypes, updateItem } from "../../api/masterItems";
import { branchCategories, branchCenters } from "../../api/gl";
import { listBranches } from "../../api/directory";
import { localName } from "../../lib/localName";

const NUM = ["min_stock", "max_stock", "reorder_point", "reference_price", "rate_per_ton_km"];
const EMPTY = { name_ar: "", name_en: "", description: "", brand: "", unit: "pcs", item_type: "inventory",
                accounting_category: "", asset_account_code: "", expense_account_code: "",
                default_cost_center_id: "", min_stock: "", max_stock: "", reorder_point: "",
                reference_price: "", tolerance_percent: 15, pricing_mode: "fixed", rate_per_ton_km: "" };

// نموذج المادة المركزية — الفرع يحدد النظام المحاسبي، والتصنيفات والحسابات ومراكز التكلفة تُجلب منه.
export const ItemForm = ({ item, onDone, onCancel }) => {
  const { t } = useTranslation();
  const { branchId } = useBranchScope();
  const [form, setForm] = useState({ ...EMPTY, ...(item || {}), branch_id: branchId });
  const [meta, setMeta] = useState({ types: [], units: [], categories: [], centers: [], branches: [] });
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const branch = meta.branches.find((row) => row.id === branchId);
  const branchCurrency = branch?.primary_currency || branch?.payroll?.currency || null;

  useEffect(() => {
    Promise.all([listItemTypes(), listBranches()])
      .then(([types, branches]) => setMeta((prev) => ({ ...prev, types: types.types,
        units: types.units, branches: branches || [] })))
      .catch(() => toast.error(t("errors.generic")));
  }, [t]);

  useEffect(() => {
    if (!branchId) { setMeta((prev) => ({ ...prev, categories: [], centers: [] })); return; }
    Promise.all([branchCategories(branchId), branchCenters(branchId)])
      .then(([cats, centers]) => setMeta((prev) => ({ ...prev, categories: cats.items || [],
        centers: centers.items || [] })))
      .catch(() => toast.error(t("errors.generic")));
  }, [branchId, t]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, branch_id: branchId }));
  }, [branchId]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, branch_id: branchId };
      NUM.forEach((k) => { payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]); });
      payload.tolerance_percent = Number(payload.tolerance_percent || 15);
      ["name_en", "description", "brand", "default_cost_center_id"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      if (item?.item_id) await updateItem(item.item_id, payload);
      else await createItem(payload);
      toast.success(t("purchases.items.saved"));
      onDone?.();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || t(`purchases.items.errors.${detail?.code}`, detail?.code || t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const invalid = !branchId || (form.name_ar || "").trim().length < 2 || !form.accounting_category || !!dup?.exact;

  return (
    <FormPageShell testid="item-form" onBack={onCancel} onCancel={onCancel} onSubmit={save} busy={busy}
                   title={item?.item_id ? t("purchases.items.editItem") : t("purchases.items.newItem")}
                   subtitle={item?.item_code || t("purchases.tabs.items")} disabled={invalid}>
      {!branchId ? (
        <div data-testid="item-branch-banner"
             className="flex items-center gap-2 rounded-2xl bg-amber-500/10 px-4 py-3 text-xs font-bold text-amber-700">
          ⚠ {t("purchases.items.pickBranchFirst")}
        </div>
      ) : null}
      <ItemSectionBasic form={form} setForm={setForm} units={meta.units} branchId={branchId}
                        excludeId={item?.item_id} onDuplicate={setDup} />
      <ItemSectionPrice form={form} setForm={setForm} branchCurrency={branchCurrency}
                        branchName={localName(branch)} />
      <ItemSectionAccounting form={form} setForm={setForm} meta={meta} />
      <ItemSectionStock form={form} setForm={setForm} centers={meta.centers} />
    </FormPageShell>
  );
};
